import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Modal from "@mui/material/Modal";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Grid from "@mui/material/Grid";
import ListItemText from "@mui/material/ListItemText";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearIcon from "@mui/icons-material/Clear";

import { useUser } from "../../Context/UserContext";
import { useNavigate } from "react-router-dom";
import "./OfficerDashboard.css";

import PostApi, { GetApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";
import DynamicOffcanvas from "../Dynamic/DynamicOffcanvas";
import DynamicField from "../Dynamic/DynamicField";
import DynamicModal from "../Dynamic/DynamicModal";
import DynamicTable from "../Dynamic/DynamicTable";

// --- ICONS ---
import FactoryIcon from "@mui/icons-material/Factory";
import GroupIcon from "@mui/icons-material/Group";
import NatureIcon from "@mui/icons-material/Nature";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import LockIcon from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";

// --- Recharts Imports ---
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
} from "recharts";

// --- Map Imports ---
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  GeoJSON,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

// --- Map Style ---
const tnStyle = {
  fillColor: "#e8f5e9",
  weight: 2,
  opacity: 1,
  color: "#1b5e20",
  fillOpacity: 0.3,
};
const maskStyle = {
  fillColor: "#f0f4f0",
  fillOpacity: 1,
  color: "transparent",
  weight: 0,
};
const PIE_COLORS = ["#1b5e20", "#2e7d32", "#00695c", "#43a047", "#558b2f"];

const CustomChartTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper
        sx={{
          p: 2,
          bgcolor: "white",
          border: "1px solid #ddd",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="primary"
          gutterBottom
        >
          Date: {data.date}
        </Typography>
        <Typography variant="body2">
          Saplings Requested: <b>{data.orders_count || 0}</b>
        </Typography>
        <Typography variant="body2" sx={{ color: "#1b5e20" }}>
          Distributed Saplings: <b>{data.billed || 0}</b>
        </Typography>
        <Typography variant="body2" sx={{ color: "#ff9800" }}>
          Pending Saplings: <b>{data.pending || 0}</b>
        </Typography>
        <Typography variant="body2" sx={{ color: "#2196f3" }}>
          Prod. Centers: <b>{data.production_centers_count || 0}</b>
        </Typography>
      </Paper>
    );
  }
  return null;
};

const createTnMask = (tnFeature) => {
  const outerPolygon = [
    [50, 0],
    [90, 0],
    [90, 35],
    [50, 35],
    [50, 0],
  ];
  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [outerPolygon, ...tnFeature.geometry.coordinates],
    },
  };
};

const OfficerDashboard = () => {
  const { token, authData, role } = useUser();
  const navigate = useNavigate();

  // Map States
  const [tnGeoData, setTnGeoData] = useState(null);
  const [tnMaskData, setTnMaskData] = useState(null);

  // Data States
  const [dashboardCounts, setDashboardCounts] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [mapCenters, setMapCenters] = useState([]);
  const [farmerMapCenters, setFarmerMapCenters] = useState([]);
  const [pcDistrictCounts, setPcDistrictCounts] = useState([]);
  const [farmerDistrictCounts, setFarmerDistrictCounts] = useState([]);
  const [farmerOrders, setFarmerOrders] = useState([]);
  const [farmerRequests, setFarmerRequests] = useState([]);

  // Loading States
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [loadingChart, setLoadingChart] = useState(false);
  const [loadingMap, setLoadingMap] = useState(true);
  const [loadingFarmerMap, setLoadingFarmerMap] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Filter States
  const [prodTypeFilter, setProdTypeFilter] = useState("all");
  const [tableStatusFilter, setTableStatusFilter] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState(""); // Default set in useEffect
  const [districtList, setDistrictList] = useState([]);

  // Target States
  const [targetLevel, setTargetLevel] = useState("department");
  const [targetData, setTargetData] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetPage, setTargetPage] = useState(0);
  const [targetSearch, setTargetSearch] = useState("");
  const itemsPerPage = 3;

  // Date States
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const [startDate, setStartDate] = useState(
    sevenDaysAgo.toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  // Registration States
  const [registerOpen, setRegisterOpen] = useState(false);
  const [formData, setFormData] = useState({
    officerName: "",
    gender: "Male",
    mobile: "",
    email: "",
    department: "",
    designation: "",
    role: "",
    username: "",
    password: "",
  });
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // Modals & Offcanvas
  const [addModal, setAddModal] = useState({
    open: false,
    field: "",
    value: "",
  });
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Production Center Assignment
  const [pcList, setPcList] = useState([]);
  const [schemeList, setSchemeList] = useState([]);
  const [pcSchemeSelection, setPcSchemeSelection] = useState({});
  const [pcAssignedStatus, setPcAssignedStatus] = useState({});
  const [loadingCenters, setLoadingCenters] = useState(false);

  // --- HELPERS ---
  const formatDateForApi = (dateStr) => {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getRoleParams = () => {
    const params = { role: authData?.role };
    if (authData?.role === "department_admin")
      params.department_id = authData?.department_id;
    if (authData?.role === "district_admin")
      params.district_id = authData?.district_id;
    if (authData?.role === "block_admin") params.block_id = authData?.block_id;
    return params;
  };

  const getTargetFilterOptions = () => {
    const r = authData?.role;
    if (r === "superadmin")
      return ["department", "district", "block", "productioncenter"];
    if (r === "department_admin")
      return ["district", "block", "productioncenter"];
    if (r === "district_admin") return ["block", "productioncenter"];
    if (r === "block_admin") return ["productioncenter"];
    return [];
  };

  const getTargetDisplayInfo = (item, level) => {
    let primaryName = "";
    let immediateParent = "";
    let grandParent = "";
    switch (level) {
      case "department":
        primaryName = item.department_name || "N/A";
        break;
      case "district":
        primaryName = item.district_name || "N/A";
        immediateParent = item.department_name || "";
        break;
      case "block":
        primaryName = item.block_name || "N/A";
        immediateParent = item.district_name || "";
        grandParent = item.department_name || "";
        break;
      case "productioncenter":
        primaryName = item.production_center_name || "N/A";
        immediateParent = item.block_name || "";
        grandParent = item.district_name || "";
        break;
      default:
        primaryName =
          item.department_name ||
          item.district_name ||
          item.block_name ||
          item.production_center_name ||
          "N/A";
    }
    return { primaryName, immediateParent, grandParent };
  };

  const getStatusColor = (s) => {
    switch ((s || "").toLowerCase().replace(/[_\s-]/g, "")) {
      case "billed":
        return "success";
      case "approved":
        return "primary";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      case "partial":
        return "info";
      case "orderplaced":
        return "primary";
      default:
        return "default";
    }
  };
  const statusOptions = ["Pending", "Order Placed", "Billed"];

  // --- FILTERED DATA LOGIC ---
  const filteredFarmerRequests = useMemo(() => {
    let data = farmerRequests;
    if (!tableStatusFilter && !selectedDistrict) return data;
    return data.filter((r) => {
      const statusMatch =
        !tableStatusFilter ||
        (r.order_status || "").toLowerCase().replace(/[_\s-]/g, "") ===
          tableStatusFilter.toLowerCase().replace(/[_\s-]/g, "");
      const districtMatch =
        !selectedDistrict ||
        String(r.farmer_details?.district_id) === String(selectedDistrict);
      return statusMatch && districtMatch;
    });
  }, [farmerRequests, tableStatusFilter, selectedDistrict]);

  const filteredTargetData = useMemo(() => {
    return targetData.filter((i) => {
      if (!targetSearch) return true;
      return (
        (
          i.department_name ||
          i.district_name ||
          i.block_name ||
          i.production_center_name ||
          ""
        )
          .toLowerCase()
          .includes(targetSearch.toLowerCase()) ||
        String(i.target_quantity)
          .toLowerCase()
          .includes(targetSearch.toLowerCase())
      );
    });
  }, [targetData, targetSearch]);

  const filteredMapCenters = useMemo(
    () =>
      mapCenters.filter(
        (c) => prodTypeFilter === "all" || c.production_type === prodTypeFilter,
      ),
    [mapCenters, prodTypeFilter],
  );

  const farmerInspectionLocations = useMemo(() => {
    return farmerOrders
      .map((o) => {
        const l = o.farmer?.location || o.location;
        if (l?.latitude && l?.longitude)
          return {
            id: o.order_id || o.id,
            name: o.farmer?.name || o.farmer_name || "Unknown",
            farmer_id: o.farmer?.id || o.farmer_id,
            type: o.scheme?.name || "N/A",
            village_name: l.village?.trim() || "N/A",
            block_name: l.block?.trim() || "N/A",
            district_name: l.district?.trim() || "Unknown",
            latitude: l.latitude,
            longitude: l.longitude,
          };
        return null;
      })
      .filter(Boolean);
  }, [farmerOrders]);

  const currentTargetItems = filteredTargetData.slice(
    targetPage * itemsPerPage,
    (targetPage + 1) * itemsPerPage,
  );

  const requestColumns = useMemo(
    () => [
      { field: "orderid", headerName: "Order ID" },
      {
        field: "farmer_name",
        headerName: "Name",
        render: (row) => row.farmer_details?.farmer_name || "N/A",
      },
      {
        field: "farmer_mobile",
        headerName: "Mobile",
        render: (row) => row.farmer_details?.farmer_mobile || "N/A",
      },
      {
        field: "order_status",
        headerName: "Status",
        render: (row) => (
          <Chip
            label={row.order_status}
            size="small"
            color={getStatusColor(row.order_status)}
            sx={{ textTransform: "capitalize", fontWeight: 600 }}
          />
        ),
      },
      {
        field: "order_date",
        headerName: "Order Date",
        render: (row) => formatDisplayDate(row.order_date),
      },
    ],
    [],
  );

  const personalFields = [
    { name: "officerName", label: "Officer Name", required: true },
    {
      name: "gender",
      label: "Gender",
      type: "select",
      required: true,
      options: [
        { id: "Male", name: "Male" },
        { id: "Female", name: "Female" },
      ],
    },
    { name: "mobile", label: "Mobile Number", required: true },
    { name: "email", label: "Email Address", type: "email" },
  ];
  const officialFields = [
    {
      name: "department",
      label: "Department",
      type: "select",
      required: true,
      options: departments,
    },
    {
      name: "designation",
      label: "Designation",
      type: "select",
      required: true,
      options: designations,
    },
    {
      name: "role",
      label: "Role",
      type: "select",
      required: true,
      options: roles,
    },
  ];
  const accountFields = [
    { name: "username", label: "Username", required: true },
    { name: "password", label: "Password", type: "password", required: true },
  ];

  // --- API CALLS ---
  useEffect(() => {
    const o = getTargetFilterOptions();
    if (o.length > 0) setTargetLevel(o[0]);
  }, [authData?.role]);

  useEffect(() => {
    const fetchCounts = async () => {
      if (!authData?.role) return;
      setLoadingCounts(true);
      try {
        const p = new URLSearchParams(getRoleParams()).toString();
        const r = await GetApi(`/users/dashboard-counts?${p}`, token);
        if (r.success) setDashboardCounts(r.data);
      } catch (e) {
      } finally {
        setLoadingCounts(false);
      }
    };
    fetchCounts();
  }, [authData, token]);

  useEffect(() => {
    const fetchChart = async () => {
      if (!authData?.role || !startDate || !endDate) return;
      setLoadingChart(true);
      try {
        const rp = new URLSearchParams(getRoleParams()).toString();
        const r = await GetApi(
          `/users/farmer-request-weekly-report?start_date=${formatDateForApi(startDate)}&end_date=${formatDateForApi(endDate)}&${rp}`,
          token,
        );
        if (r.success && r.data) {
          setChartData(
            r.data.map((i) => ({
              ...i,
              name: new Date(i.date).toLocaleDateString("en-US", {
                weekday: "short",
              }),
              orders_count: Number(i.orders_count) || 0,
              billed: Number(i.billed) || 0,
              pending: Number(i.pending) || 0,
            })),
          );
        }
      } catch (e) {
      } finally {
        setLoadingChart(false);
      }
    };
    fetchChart();
  }, [startDate, endDate, authData, token]);

  useEffect(() => {
    const fetchTargets = async () => {
      if (!authData?.role || !targetLevel) return;
      setLoadingTargets(true);
      try {
        const params = new URLSearchParams(getRoleParams()).toString();
        const r = await GetApi(
          `/users/target-details/?${params}&target_level=${targetLevel}`,
          token,
        );
        if (r.success && r.data) {
          setTargetData(r.data);
          setTargetPage(0);
          setTargetSearch("");
        } else setTargetData([]);
      } catch (e) {
        setTargetData([]);
      } finally {
        setLoadingTargets(false);
      }
    };
    fetchTargets();
  }, [targetLevel, authData, token]);

  useEffect(() => {
    const loadGeoJson = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_10m_admin_1_states_provinces_india.geojson",
        );
        const data = await response.json();
        let tnFeatureToUse = data.features.find(
          (f) =>
            f.properties.name === "Tamil Nadu" ||
            f.properties.name === "Tamil Nādu",
        );
        if (!tnFeatureToUse) {
          const response2 = await fetch(
            "https://raw.githubusercontent.com/Subhash9465/GeoJson-Data-of-Indian-States/master/Tamil_Nadu.json",
          );
          const data2 = await response2.json();
          tnFeatureToUse =
            data2.features?.[0] || (data2.type === "Feature" ? data2 : null);
        }
        if (tnFeatureToUse) {
          setTnGeoData({
            type: "FeatureCollection",
            features: [tnFeatureToUse],
          });
          setTnMaskData({
            type: "FeatureCollection",
            features: [createTnMask(tnFeatureToUse)],
          });
        }
      } catch (err) {
        console.error(err);
      }
    };
    const fetchProdCenters = async () => {
      if (!authData?.role) return;
      setLoadingMap(true);
      try {
        const params = new URLSearchParams(getRoleParams()).toString();
        const res = await GetApi(`/users/production-centers?${params}`, token);
        if (res.success && res.data) {
          const approved = res.data.filter((c) => c.status === "approved");
          const counts = approved.reduce((acc, c) => {
            const d = c.District_Name || "Unknown";
            acc[d] = (acc[d] || 0) + 1;
            return acc;
          }, {});
          setPcDistrictCounts(
            Object.entries(counts)
              .map(([name, count]) => ({ name, count }))
              .sort((a, b) => b.count - a.count),
          );
          setMapCenters(approved.filter((c) => c.latitude && c.longitude));
        }
      } catch (e) {
      } finally {
        setLoadingMap(false);
      }
    };
    const fetchFarmerDetails = async () => {
      if (!authData?.role) return;
      setLoadingFarmerMap(true);
      try {
        const params = new URLSearchParams(getRoleParams()).toString();
        const res = await GetApi(`/users/farmer-details/?${params}`, token);
        if (res.success && res.data) {
          const counts = res.data.reduce((acc, f) => {
            const d = f.district_name || "Unknown";
            acc[d] = (acc[d] || 0) + 1;
            return acc;
          }, {});
          setFarmerDistrictCounts(
            Object.entries(counts)
              .map(([name, count]) => ({ name, count }))
              .sort((a, b) => b.count - a.count),
          );
          setFarmerMapCenters(
            res.data.filter((f) => f.latitude && f.longitude),
          );
        }
      } catch (e) {
      } finally {
        setLoadingFarmerMap(false);
      }
    };
    loadGeoJson();
    fetchProdCenters();
    fetchFarmerDetails();
  }, [authData, token]);

  // --- FETCH DISTRICTS & SET DEFAULT ---
  useEffect(() => {
    const fetchDistricts = async () => {
      if (!token) return;
      try {
        // Call the API provided
        const res = await GetApi("/master/districts", token);

        // Handle the response: Based on your snippet, it returns a direct array,
        // but sometimes APIs wrap it in { success: true, data: [...] }.
        // We handle both cases to be safe.
        let districts = [];
        if (Array.isArray(res)) {
          districts = res;
        } else if (res.success && Array.isArray(res.data)) {
          districts = res.data;
        }

        setDistrictList(districts);

        // Set first district as default if data exists
        if (districts.length > 0) {
          setSelectedDistrict(districts[0].id); // Sets ID 30 (Ariyalur) as default
        }
      } catch (err) {
        console.error("Failed to fetch districts", err);
      }
    };
    fetchDistricts();
  }, [token]);

  // --- FETCH REQUESTS (With District & Status Filters) ---
  // useEffect(() => {
  //   const fetchRequests = async () => {
  //     if (!authData?.role) return;
  //     setLoadingRequests(true);
  //     try {
  //       const params = new URLSearchParams();
  //       // Send district_id and status to backend
  //       if (selectedDistrict) params.append("district_id", selectedDistrict);
  //       if (tableStatusFilter) params.append("status", tableStatusFilter);

  //       const url = `/users/center-orders?${params.toString()}`;

  //       const r = await GetApi(url, token);
  //       setFarmerRequests(r.results || []);
  //     } catch (e) {
  //       console.error(e);
  //       setFarmerRequests([]);
  //     } finally {
  //       setLoadingRequests(false);
  //     }
  //   };
  //   fetchRequests();
  // }, [authData, token, selectedDistrict, tableStatusFilter]);

  // --- FETCH REQUESTS (With District & Status Filters) ---
  useEffect(() => {
    const fetchRequests = async () => {
      if (!authData?.role) return;
      setLoadingRequests(true);
      try {
        const params = new URLSearchParams();

        // Send district_id and status to backend
        if (selectedDistrict) params.append("district_id", selectedDistrict);
        if (tableStatusFilter) params.append("status", tableStatusFilter);

        // FIX: Changed from "/users/center-orders" to "/users/farmer-request"
        // to match your existing Router: router.get("/farmer-request/", ...)
        const url = `/users/farmer-request?${params.toString()}`;

        const r = await GetApi(url, token);

        // Handle response based on your controller's output structure
        if (r && r.results) {
          setFarmerRequests(r.results);
        } else if (Array.isArray(r)) {
          // Fallback if controller returns array directly
          setFarmerRequests(r);
        } else {
          setFarmerRequests([]);
        }
      } catch (e) {
        console.error("Failed to fetch requests", e);
        setFarmerRequests([]);
      } finally {
        setLoadingRequests(false);
      }
    };
    fetchRequests();
  }, [authData, token, selectedDistrict, tableStatusFilter]);
  useEffect(() => {
    const fetchFarmerOrders = async () => {
      if (!authData?.user_id || !authData?.role) return;
      setLoadingFarmerMap(true);
      try {
        const url = `${API_URLS.GetFormersOrders}${authData.user_id}/${authData.role}`;
        const res = await GetApi(url, token);
        const orders = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || [];
        setFarmerOrders(orders);
        const countsObj = orders.reduce((acc, o) => {
          const d = o.farmer?.location?.district?.trim() || "Unknown";
          acc[d] = (acc[d] || 0) + 1;
          return acc;
        }, {});
        setFarmerDistrictCounts(
          Object.entries(countsObj).map(([district, count]) => ({
            district,
            count,
          })),
        );
      } catch (err) {
      } finally {
        setLoadingFarmerMap(false);
      }
    };
    fetchFarmerOrders();
  }, [authData.user_id, authData.role, token]);

  useEffect(() => {
    const fetchAssignmentData = async () => {
      if (!authData?.user_id || !authData?.role) return;
      setLoadingCenters(true);
      try {
        const centerRes = await GetApi(
          `/officers/get-producion-center/${authData.user_id}/${authData.role}`,
          token,
        );
        const schemeRes = await GetApi("/officers/get-all-schemes", token);
        if (centerRes.success && centerRes.data) {
          setPcList(centerRes.data);
          const initSel = {},
            initStat = {};
          centerRes.data.forEach((pc) => {
            initSel[pc.id] = pc.scheme_ids || [];
            initStat[pc.id] = pc.scheme_ids && pc.scheme_ids.length > 0;
          });
          setPcSchemeSelection(initSel);
          setPcAssignedStatus(initStat);
        }
        if (schemeRes.success && schemeRes.data) setSchemeList(schemeRes.data);
      } catch (err) {
      } finally {
        setLoadingCenters(false);
      }
    };
    fetchAssignmentData();
  }, [authData.user_id, authData.role, token]);

  const fetchDropdownData = async () => {
    setDropdownLoading(true);
    try {
      const [dr, ds, rr] = await Promise.all([
        GetApi("/officers/departments", token),
        GetApi("/officers/designations", token),
        GetApi("/users/roles", token),
      ]);
      const fd = (d) =>
        !d
          ? []
          : d.map((x) => ({
              id: x._id || x.id || x.name || x,
              name:
                x.name ||
                x.departmentName ||
                x.designationName ||
                x.roleName ||
                x,
            }));
      setDepartments(fd(dr));
      setDesignations(fd(ds));
      setRoles(fd(rr));
    } catch (e) {
    } finally {
      setDropdownLoading(false);
    }
  };
  useEffect(() => {
    if (registerOpen) fetchDropdownData();
  }, [registerOpen, token]);

  // --- EVENT HANDLERS ---
  const handleNextPage = () => {
    if ((targetPage + 1) * itemsPerPage < filteredTargetData.length)
      setTargetPage(targetPage + 1);
  };
  const handlePrevPage = () => {
    if (targetPage > 0) setTargetPage(targetPage - 1);
  };
  const handleOpenRequestModal = (r) => {
    setSelectedRequest(r);
    setRequestModalOpen(true);
  };
  const handleCloseRequestModal = () => {
    setRequestModalOpen(false);
    setSelectedRequest(null);
  };
  const handleInputChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();
    if (
      !formData.username ||
      !formData.password ||
      !formData.email ||
      !formData.officerName ||
      !formData.mobile ||
      !formData.department
    ) {
      alert("Fill all fields");
      return;
    }
    try {
      await PostApi(
        "/officers/register",
        {
          username: formData.username,
          password: formData.password,
          email: formData.email,
          officername: formData.officerName,
          gender: formData.gender,
          mobile: formData.mobile,
          department: formData.department,
          designation: formData.designation,
          role: formData.role,
        },
        token,
      );
      alert("Success!");
      setRegisterOpen(false);
      setFormData({
        officerName: "",
        gender: "Male",
        mobile: "",
        email: "",
        department: "",
        designation: "",
        role: "",
        username: "",
        password: "",
      });
    } catch (e) {
      alert("Failed.");
    }
  };
  const handleOpenAddModal = (f) =>
    setAddModal({ open: true, field: f, value: "" });
  const handleCloseAddModal = () =>
    setAddModal({ open: false, field: "", value: "" });
  const handleAddNewItem = async () => {
    const { field, value } = addModal;
    if (!value.trim()) return;
    try {
      if (field === "department") {
        await PostApi("officers/departments", { name: value }, token);
        setDepartments((p) => [...p, { id: value, name: value }]);
        setFormData((p) => ({ ...p, department: value }));
      } else if (field === "designation") {
        await PostApi("officers/designations", { name: value }, token);
        setDesignations((p) => [...p, { id: value, name: value }]);
        setFormData((p) => ({ ...p, designation: value }));
      }
      handleCloseAddModal();
    } catch (e) {
      alert("Failed");
    }
  };
  const handleSchemeChange = (event, centerId) => {
    const {
      target: { value },
    } = event;
    setPcSchemeSelection((prev) => ({
      ...prev,
      [centerId]: typeof value === "string" ? value.split(",") : value,
    }));
  };
  const handleAssignSchemes = async (centerId) => {
    const selectedSchemes = pcSchemeSelection[centerId] || [];
    if (selectedSchemes.length === 0) {
      alert("Please select at least one scheme.");
      return;
    }
    try {
      await PostApi(
        "/officers/assign-schemes",
        { center_id: centerId, scheme_ids: selectedSchemes },
        token,
      );
      setPcAssignedStatus((prev) => ({ ...prev, [centerId]: true }));
      alert("Schemes assigned successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to assign schemes.");
    }
  };

  // --- UI SUB-COMPONENTS ---
  const renderFormSection = (title, icon, fields) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        {icon}
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="text.secondary"
          textTransform="uppercase"
          letterSpacing={0.5}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {fields.map((f) => (
          <Box
            key={f.name}
            sx={{
              width: "100%",
              "@media (min-width: 900px)": { width: "calc(50% - 8px)" },
            }}
          >
            <DynamicField
              field={f}
              value={formData[f.name]}
              onChange={handleInputChange}
              loading={dropdownLoading}
              options={f.options}
              sx={{ width: "100%" }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );

  const offcanvasActions = (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Button
        variant="outlined"
        fullWidth
        color="error"
        onClick={() => setRegisterOpen(false)}
        sx={{ fontWeight: 700, py: 1.2 }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ bgcolor: "#1b5e20", color: "#fff", fontWeight: 700, py: 1.2 }}
        onClick={handleFormSubmit}
      >
        Submit
      </Button>
    </Box>
  );

  const BottomLegend = ({ label, color, data, dataKey = "name" }) => {
    const [expanded, setExpanded] = useState(false);
    return (
      <Paper
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: "rgba(255,255,255,0.95)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            bgcolor: "#fff",
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: color,
                boxShadow: "0 0 4px rgba(0,0,0,0.2)",
              }}
            />
            <Typography variant="caption" fontWeight={700}>
              {label}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "10px" }}
            >
              {data.reduce((sum, d) => sum + d.count, 0)} Total
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transition: "transform 0.3s",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>
        </Box>
        {expanded && (
          <Box
            sx={{
              p: "10px 16px",
              borderTop: "1px solid #eee",
              bgcolor: "#fff",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.8,
                width: "100%",
                pb: 0.5,
              }}
            >
              {data.length > 0 ? (
                data.map((item, idx) => (
                  <Chip
                    key={idx}
                    size="small"
                    label={`${item[dataKey]}: ${item.count}`}
                    sx={{
                      fontSize: "10px",
                      height: 22,
                      bgcolor: "#f5f5f5",
                      border: `1px solid ${color}20`,
                      fontWeight: 600,
                    }}
                  />
                ))
              ) : (
                <Typography
                  variant="caption"
                  sx={{ fontStyle: "italic", color: "#999" }}
                >
                  No data
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  // --- MAIN RENDER ---
  return (
    <Box className="officer-dashboard-container">
      <Box className="dashboard-header">
        <Box>
          <Typography variant="h4" sx={{ color: "#1B4E20", fontWeight: 800 }}>
            Welcome {authData.user_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            State-wide Overview & GIS Analytics
          </Typography>
        </Box>
        <Box className="header-actions">
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate("/officer-registartion")}
            sx={{
              bgcolor: "#1b5e20",
              color: "#fff",
              fontWeight: 700,
              "&:hover": { bgcolor: "#005500" },
            }}
          >
            Register
          </Button>
          {(role == "superadmin" || role == "department_admin") && (
            <>
              <Button
                variant="contained"
                startIcon={<AssessmentIcon />}
                onClick={() => navigate("/department-report")}
                sx={{ bgcolor: "#ffc022", color: "#000", fontWeight: 700 }}
              >
                View Reports
              </Button>
              <Button
                variant="contained"
                startIcon={<AssessmentIcon />}
                onClick={() => navigate("/DistrictsaplingReport")}
                sx={{ bgcolor: "#2e7d32", color: "#fff", fontWeight: 700 }}
              >
                Sapling Report
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* 1. STATS CARDS */}
      {loadingCounts ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="dashboard-row row-stats">
          {dashboardCounts && (
            <>
              {authData?.role === "superadmin" && (
                <div className="card-stat">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      borderLeft: "4px solid #1b5e20",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <BusinessIcon
                      sx={{ color: "#1b5e20", fontSize: 32, mb: 1 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      Dept Admins
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {dashboardCounts.department_admin_count}
                    </Typography>
                  </Paper>
                </div>
              )}
              {["superadmin", "department_admin"].includes(authData?.role) && (
                <div className="card-stat">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      borderLeft: "4px solid #2e7d32",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <BadgeIcon sx={{ color: "#2e7d32", fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" fontWeight={600}>
                      District Admins
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {dashboardCounts.district_admin_count}
                    </Typography>
                  </Paper>
                </div>
              )}
              {["superadmin", "department_admin", "district_admin"].includes(
                authData?.role,
              ) && (
                <div className="card-stat">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      borderLeft: "4px solid #00695c",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <AdminPanelSettingsIcon
                      sx={{ color: "#00695c", fontSize: 32, mb: 1 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      Block Admins
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {dashboardCounts.block_admin_count}
                    </Typography>
                  </Paper>
                </div>
              )}
              <div className="card-stat">
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    borderLeft: "4px solid #7cb342",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <FactoryIcon sx={{ color: "#7cb342", fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    Prod. Centers
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {dashboardCounts.production_centers_count}
                  </Typography>
                </Paper>
              </div>
              <div className="card-stat">
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    borderLeft: "4px solid #ffc022",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <GroupIcon sx={{ color: "#ffc022", fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    Benifit Farmers
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {dashboardCounts.farmers_count}
                  </Typography>
                </Paper>
              </div>
              <div className="card-stat">
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    borderLeft: "4px solid #ff9800",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <NatureIcon sx={{ color: "#ff9800", fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>
                    Species
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {dashboardCounts.species_in_stock_count}
                  </Typography>
                </Paper>
              </div>
            </>
          )}
        </div>
      )}

      {/* 2. CHARTS ROW */}
      <div className="dashboard-row row-charts">
        <div className="card-chart">
          <Paper
            sx={{
              pb: "0px",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              className="chart-header"
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
              }}
            >
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                Distribution Activity
              </Typography>
              <Box
                className="date-filters"
                sx={{
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  gap: 1,
                }}
              >
                <TextField
                  sx={{ flex: 1 }}
                  label="Start Date"
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography>to</Typography>
                <TextField
                  sx={{ flex: 1 }}
                  label="End Date"
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
            {loadingChart ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={300}
                flexGrow={1}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1, minHeight: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <Tooltip content={<CustomChartTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="orders_count"
                      name="Orders"
                      fill="#2196f3"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="billed"
                      name="Billed"
                      fill="#1b5e20"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="pending"
                      name="Pending"
                      fill="#ff9800"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </div>

        {/* TARGETS PIE CHART */}
        <div className="card-chart">
          <Paper
            sx={{
              pb: "8px",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
            }}
          >
            <Box className="chart-header" sx={{ flexWrap: "wrap", gap: 2 }}>
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                Targets Overview
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  "@media (min-width: 900px)": { width: "auto" },
                }}
              >
                <FormControl size="small" sx={{ minWidth: 120, flexGrow: 1 }}>
                  <InputLabel>Filter Level</InputLabel>
                  <Select
                    value={targetLevel}
                    label="Filter Level"
                    onChange={(e) => setTargetLevel(e.target.value)}
                  >
                    {getTargetFilterOptions().map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={targetSearch}
                  onChange={(e) => {
                    setTargetSearch(e.target.value);
                    setTargetPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1, maxWidth: 200 }}
                />
              </Box>
            </Box>
            {loadingTargets ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height={300}
                flexGrow={1}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    flexGrow: 1,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {filteredTargetData.length > itemsPerPage && (
                    <IconButton
                      onClick={handlePrevPage}
                      disabled={targetPage === 0}
                      sx={{
                        position: "absolute",
                        left: -20,
                        zIndex: 1,
                        bgcolor: "white",
                        boxShadow: 1,
                      }}
                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: "100%",
                      overflow: "hidden",
                      px: 1,
                      justifyContent: "center",
                    }}
                  >
                    {currentTargetItems.length > 0 ? (
                      currentTargetItems.map((item, index) => {
                        const { primaryName, immediateParent, grandParent } =
                          getTargetDisplayInfo(item, targetLevel);
                        return (
                          <Box
                            key={item.id || index}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              p: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 100,
                                height: 100,
                                position: "relative",
                              }}
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={[{ value: 1 }]}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={35}
                                    outerRadius={45}
                                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                                    paddingAngle={5}
                                    dataKey="value"
                                  />
                                  <text
                                    x="50%"
                                    y="50%"
                                    textAnchor="middle"
                                    dy=".3em"
                                    fontSize="12"
                                    fontWeight="bold"
                                    fill="#333"
                                  >
                                    {item.target_quantity}
                                  </text>
                                </PieChart>
                              </ResponsiveContainer>
                            </Box>
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              textAlign="center"
                              mt={1}
                              noWrap
                              sx={{ width: "100%" }}
                              title={`${primaryName}${immediateParent ? ` — ${immediateParent}` : ""}${grandParent ? ` — ${grandParent}` : ""}`}
                            >
                              {primaryName}
                            </Typography>
                            {immediateParent && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "9px",
                                  fontWeight: 600,
                                  color: "#555",
                                  textAlign: "center",
                                  display: "block",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={immediateParent}
                              >
                                {immediateParent}
                              </Typography>
                            )}
                            {targetLevel === "department" && (
                              <Typography variant="caption">
                                {item?.scheme_type}
                              </Typography>
                            )}

                            {grandParent && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "10px",
                                  color: "#888",
                                  textAlign: "center",
                                  display: "block",
                                  maxWidth: "120px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={grandParent}
                              >
                                {grandParent}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              textAlign="center"
                              display="block"
                              sx={{ mt: 0.3 }}
                            >
                              {role === "superadmin" &&
                              targetLevel === "department"
                                ? item.financial_year || "N/A"
                                : `${formatDisplayDate(item.start_date)} - ${formatDisplayDate(item.end_date)}`}
                            </Typography>
                            <Chip
                              label={item.created_by_name || "System"}
                              size="small"
                              sx={{ mt: 0.5, height: 20, fontSize: "10px" }}
                            />
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height={200}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No targets found
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {filteredTargetData.length > itemsPerPage && (
                    <IconButton
                      onClick={handleNextPage}
                      disabled={
                        (targetPage + 1) * itemsPerPage >=
                        filteredTargetData.length
                      }
                      sx={{
                        position: "absolute",
                        right: -20,
                        zIndex: 1,
                        bgcolor: "white",
                        boxShadow: 1,
                      }}
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {filteredTargetData.length > itemsPerPage && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    {Array.from(
                      {
                        length: Math.ceil(
                          filteredTargetData.length / itemsPerPage,
                        ),
                      },
                      (_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: i === targetPage ? "#1b5e20" : "#ddd",
                            mx: 0.5,
                            cursor: "pointer",
                          }}
                          onClick={() => setTargetPage(i)}
                        />
                      ),
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </div>
      </div>

      {/* 3. MAPS ROW */}
      <div className="dashboard-row row-maps">
        <div className="card-map">
          <Paper
            sx={{
              pb: "1px",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                Approved Production Centers
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={`${filteredMapCenters.length} Plotted`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 700 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={prodTypeFilter}
                    label="Type"
                    onChange={(e) => setProdTypeFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                    <MenuItem value="government">Government</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                minHeight: "500px",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #ddd",
                position: "relative",
              }}
            >
              {loadingMap ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <MapContainer
                    center={[11.1271, 78.6569]}
                    zoom={7}
                    scrollWheelZoom={false}
                    style={{ height: "100%", width: "100%" }}
                    maxBounds={[
                      [6.0, 76.0],
                      [14.0, 81.0],
                    ]}
                    maxBoundsViscosity={1.0}
                    minZoom={6}
                    maxZoom={12}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {tnMaskData && (
                      <GeoJSON data={tnMaskData} style={maskStyle} />
                    )}
                    {tnGeoData && <GeoJSON data={tnGeoData} style={tnStyle} />}
                    {filteredMapCenters.map((center) => (
                      <CircleMarker
                        key={center.id}
                        center={[
                          parseFloat(center.latitude),
                          parseFloat(center.longitude),
                        ]}
                        radius={8}
                        pathOptions={{
                          color: "#fff",
                          weight: 2,
                          fillColor: "#1b5e20",
                          fillOpacity: 0.9,
                        }}
                      >
                        <Popup minWidth={220}>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {center.name_of_production_centre}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {center.complete_address}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Type:{" "}
                              <b style={{ textTransform: "capitalize" }}>
                                {center.production_type || "N/A"}
                              </b>
                            </Typography>
                            <Chip
                              label={center.district_name || "District"}
                              size="small"
                              sx={{ bgcolor: "#e0e0e0", fontWeight: 700 }}
                            />
                          </Box>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                  <BottomLegend
                    label="Production Centers"
                    color="#1b5e20"
                    data={pcDistrictCounts}
                  />
                </>
              )}
            </Box>
          </Paper>
        </div>

        <div className="card-map">
          <Paper
            sx={{
              pb: "1px",
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                Farmer Locations
              </Typography>
              <Chip
                label={`${farmerInspectionLocations.length} Farmers Plotted`}
                size="small"
                sx={{ fontWeight: 700, bgcolor: "#0288d1", color: "#fff" }}
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                minHeight: "500px",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #ddd",
                position: "relative",
              }}
            >
              {loadingFarmerMap ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <MapContainer
                    center={[11.1271, 78.6569]}
                    zoom={7}
                    scrollWheelZoom={false}
                    style={{ height: "100%", width: "100%" }}
                    maxBounds={[
                      [6.0, 76.0],
                      [14.0, 81.0],
                    ]}
                    maxBoundsViscosity={1.0}
                    minZoom={6}
                    maxZoom={12}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {tnMaskData && (
                      <GeoJSON data={tnMaskData} style={maskStyle} />
                    )}
                    {tnGeoData && <GeoJSON data={tnGeoData} style={tnStyle} />}
                    {farmerInspectionLocations.map((farmer) => (
                      <CircleMarker
                        key={farmer.id}
                        center={[
                          parseFloat(farmer.latitude),
                          parseFloat(farmer.longitude),
                        ]}
                        radius={8}
                        pathOptions={{
                          color: "#fff",
                          weight: 2,
                          fillColor: "#0288d1",
                          fillOpacity: 0.9,
                        }}
                      >
                        <Popup minWidth={220}>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {farmer.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              ID: {farmer.farmer_id} | Type: {farmer.type}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ mb: 1 }}
                            >
                              {farmer.village_name}, {farmer.block_name}
                            </Typography>
                            <Chip
                              label={farmer.district_name}
                              size="small"
                              sx={{ bgcolor: "#e0e0e0", fontWeight: 700 }}
                            />
                          </Box>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                  <BottomLegend
                    label="Farmers"
                    color="#0288d1"
                    data={farmerDistrictCounts}
                    dataKey="district"
                  />
                </>
              )}
            </Box>
          </Paper>
        </div>
      </div>

      {/* 4. TABLE ROW */}
      <div className="dashboard-row">
        <div style={{ width: "100%" }}>
          <Box
            mb={1}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                Recent Farmer Requests
              </Typography>
              {(tableStatusFilter || selectedDistrict) && (
                <Chip
                  label={`${filteredFarmerRequests.length} results`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={() => {
                    setTableStatusFilter("");
                    setSelectedDistrict("");
                  }}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              {/* DISTRICT FILTER */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>District</InputLabel>
                <Select
                  value={selectedDistrict}
                  label="District"
                  onChange={(e) => {
                    setSelectedDistrict(e.target.value);
                    setTargetPage(0);
                  }}
                  sx={{ borderRadius: 2, backgroundColor: "#f5f5f5" }}
                  startAdornment={
                    selectedDistrict && (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDistrict("");
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                >
                  <MenuItem value="">
                    <em>All Districts</em>
                  </MenuItem>
                  {districtList.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.District_Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* STATUS FILTER */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={tableStatusFilter}
                  label="Status"
                  onChange={(e) => setTableStatusFilter(e.target.value)}
                  sx={{ borderRadius: 2, backgroundColor: "#f5f5f5" }}
                  startAdornment={
                    tableStatusFilter && (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTableStatusFilter("");
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                >
                  <MenuItem value="">
                    <em>All Statuses</em>
                  </MenuItem>
                  {statusOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor:
                              s.toLowerCase() === "billed"
                                ? "#2e7d32"
                                : s.toLowerCase() === "order placed"
                                  ? "#1976d2"
                                  : "#ed6c02",
                          }}
                        />
                        <span style={{ textTransform: "capitalize" }}>{s}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <DynamicTable
            data={filteredFarmerRequests}
            columns={requestColumns}
            loading={loadingRequests}
            emptyMessage={
              tableStatusFilter || selectedDistrict
                ? `No requests found with current filters`
                : "No requests found"
            }
            searchKeys={[
              "farmer_details.farmer_name",
              "farmer_details.farmer_mobile",
              "orderid",
            ]}
            searchPlaceholder="Search..."
            onEdit={(row) => handleOpenRequestModal(row)}
            viewMode={true}
          />
        </div>
      </div>

      {/* 5. MODALS & OFFCANVAS */}
      <DynamicOffcanvas
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        title="New Officer Registration"
        width="500px"
        actions={offcanvasActions}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {renderFormSection(
            "Personal Information",
            <BadgeIcon sx={{ color: "#1b5e20" }} />,
            personalFields,
          )}
          <Divider sx={{ borderStyle: "dashed", my: 1 }} />
          {renderFormSection(
            "Official Details",
            <BusinessIcon sx={{ color: "#1b5e20" }} />,
            officialFields,
          )}
          <Divider sx={{ borderStyle: "dashed", my: 1 }} />
          {renderFormSection(
            "Account Security",
            <LockIcon sx={{ color: "#1b5e20" }} />,
            accountFields,
          )}
        </Box>
      </DynamicOffcanvas>

      <DynamicModal
        open={addModal.open}
        onClose={handleCloseAddModal}
        onConfirm={handleAddNewItem}
        title={`Add New ${addModal.field}`}
        confirmText="Add"
      >
        <DynamicField
          field={{
            name: "newItem",
            label: `Enter ${addModal.field} Name`,
            required: true,
          }}
          value={addModal.value}
          onChange={(e) =>
            setAddModal((p) => ({ ...p, value: e.target.value }))
          }
        />
      </DynamicModal>

      <Modal open={requestModalOpen} onClose={handleCloseRequestModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 4,
            boxShadow: 24,
            p: 4,
            outline: "none",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {selectedRequest && (
            <>
              <Typography variant="h6" fontWeight={700} mb={2} color="#1b5e20">
                Order Details: {selectedRequest.orderid}
              </Typography>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {selectedRequest.farmer_details?.farmer_name || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Mobile
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {selectedRequest.farmer_details?.farmer_mobile || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={selectedRequest.order_status}
                      size="small"
                      color={getStatusColor(selectedRequest.order_status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatDisplayDate(selectedRequest.order_date)}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Items:
              </Typography>
              <List
                dense
                sx={{ width: "100%", bgcolor: "#f9f9f9", borderRadius: 2 }}
              >
                {selectedRequest.requested_items?.map((i, idx) => (
                  <ListItem key={idx} sx={{ borderBottom: "1px solid #eee" }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={700}>
                          {i.species_name || "Unknown"}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="caption"
                            display="block"
                          >
                            Qty: {i.requested_quantity} | Approved:{" "}
                            {i.approved_quantity || "N/A"}
                          </Typography>
                          <Chip
                            label={i.item_status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 10,
                              mt: 0.5,
                              textTransform: "capitalize",
                            }}
                            color={
                              i.item_status === "approved"
                                ? "success"
                                : "warning"
                            }
                          />
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3, bgcolor: "#1b5e20" }}
                onClick={handleCloseRequestModal}
              >
                Close
              </Button>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default OfficerDashboard;
